<script setup lang="ts">
  import { computed, ref, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { custom_moment } from '@/plugins/moment-cultured';

  import { useGeneralPurposeModalStore, useSessionStore, useSurveyStore, useDashboardStore } from 'src/store';
  import { getSurveyDefinition, updateSurveyProgress } from 'src/api/endpoints/calendarWeb';
  import { handleApiRequest } from 'src/api/handleApiRequest';
  import { toSurveyDefinitionModel, toSurveyProgressModel } from 'src/services/surveys/surveyService';
  import type { SurveyProgressDenormalizedQuestionAndAnswerModel } from 'src/types/model';
  import { emptySurveyDefinition } from 'src/services/surveys/utils';
  import Box from 'src/components/BoxContainer.vue';
  import MultiViewLayout from 'src/components/layouts/MultiViewLayout.vue';
  import ProgressBar from 'src/components/ProgressBar.vue';
  import TheWizardPage from './components/TheWizardPage.vue';
  import TheSurveySubmit from './components/TheSurveySubmit.vue';
  import type { SurveyAnswerRequest, Task } from 'src/types/webContracts';
  import {
    SurveyComponentTypeEnum,
    SurveyComponentQuestionTypeEnum,
    SurveyTypeEnum,
  } from 'src/constants/survey';
  import { GeneralPurposeVariant, TaskType } from 'src/constants/enums';
  import { getPatientStatisticSummaries } from 'src/api/endpoints/statisticsWeb';
  import { getPatientProfileData } from 'src/api/endpoints/userWeb';
  import type { ITheSurveyPageComponent } from 'src/types/components';
  import { useQuasar } from 'quasar';
  import { useI18n } from 'vue-i18n';
  import { InterruptType } from '@/constants/enums';
  import {
    BoxWidthEnum
  } from 'src/constants/enums';

  const { t } = useI18n();
  const sessionStore = useSessionStore();
  const surveyModule = useSurveyStore();
  const generalPurposeModalStore = useGeneralPurposeModalStore()
  const dashboardStore = useDashboardStore()
  const router = useRouter();
  const $q = useQuasar()
  const isMagicLinkUser = computed(() => sessionStore.isMagicLinkUser);

  const page = ref({} as { value: ITheSurveyPageComponent | null });
  const isLoading = ref(true);
  const declineToComplete = ref(false);

  const props = defineProps({
    surveyId: {
      type: Number,
      required: true,
    },
    tickSignature: {
      type: String,
      required: true,
    },
    pageNumber: {
      type: String,
      required: true,
    },
    redirectRoute: {
      type: String,
      required: false,
      default: 'Dashboard.Home',
    },
  });

  const redirectRoute = ref(props.redirectRoute);

  onMounted(async () => {
    currentStep.value = 1;

    const languageId = sessionStore.user?.preferredLanguageId || 0;
    if (isMagicLinkUser.value) {
      redirectRoute.value = 'Dashboard.MLHome';
    }

    let { eventDate } =
      dashboardStore.tasks?.find((item) => item.tickSignature === props.tickSignature) || ({} as Task);

    let tickSignature = props.tickSignature;

    //fetch the questions and possible answers
    const surveyDefinitionResponse = await handleApiRequest(() =>
      getSurveyDefinition(props.surveyId, languageId, eventDate, tickSignature), null, $q
    );

    if (surveyDefinitionResponse?.result) {
      const surveyDefinitionModel = toSurveyDefinitionModel(surveyDefinitionResponse.result);
      surveyModule.setSurveyDefinition(surveyDefinitionModel);

      totalSteps.value = surveyDefinitionModel?.pages?.length;

      const surveyProgressModel = toSurveyProgressModel(
        props.surveyId,
        eventDate,
        tickSignature,
        surveyDefinitionModel,
        surveyDefinitionResponse.result
      );
      surveyModule.setSurveyProgress(surveyProgressModel);

      //Initialize a survey response
      if (surveyDefinitionResponse?.result) {
        surveyModule.setSyncPageVisibilityAndQuestionText(surveyDefinitionResponse.result.survey.pageList);
        if (props.pageNumber) {
          const page = surveyModule.surveyDefinition?.pages.find(
            (p) => p.renderingConfig.order === parseInt(props.pageNumber, 10)
          );

          surveyModule.setCurrentPage(page);
        } else {
          surveyModule.setCurrentPageToNextVisiblePage({});
        }
      }
    }

    document.querySelectorAll('html')[0].scrollTop = 0;

    isLoading.value = false;
  });

  const isProcessing = ref(false)
  const survey = computed(() => surveyModule.surveyDefinition || emptySurveyDefinition());
  const currentPage = computed(() => surveyModule.currentPage);
  const isComplete = computed(() => surveyModule.surveyProgress.isComplete);
  const buttonText = computed(() => isLastPage.value ? 'got_it_button' : 'activate_continue_text');
  const isLastPage = computed(() => surveyModule.currentPage === surveyModule.surveyDefinition?.pages.filter((p) => !p.renderingConfig.hidePage).slice(-1).pop());
  const headingText = computed(() => {
    return t(survey.value.name).toString()
  });

  const totalSteps = ref(null);
  const currentStep = ref(1)

  const isFirstPage = computed(() => {
    if (!surveyModule.currentPage.pageId) {
      return false;
    }
    const pageOrder = surveyModule.currentPage.renderingConfig.order || 0;
    return pageOrder === 10;
  });

  function onLayoutBackClick() {
    router.go(-1);
  }

  async function onBackClick() {
    currentStep.value--;
    surveyModule.setCurrentPageToPreviousPage();
    window.scrollTo(0, 0);
    (document?.activeElement as HTMLElement | null)?.blur(); // Prevent back button from retaining focus
  }

  async function onNextClick() {

    currentStep.value++;
    const surveyPage = page.value as ITheSurveyPageComponent;


    const isValid = await surveyPage.validate();
    if (isValid) {
      const languageId = sessionStore.user?.preferredLanguageId || 0;
      const responses = surveyPage
        .getResponses()
        .filter(
          (r: any) =>
            r.permittedAnswer.renderingConfig.isRecorded &&
            typeof r.value !== 'undefined' &&
            r.value !== null
        );

      // If there is an unanswered unskippable question, display error message
      if (
        currentPage.value.questions.some(
          (q) =>
            !q.renderingConfig.canSkip &&
            !responses.some((r: any) => r.question.questionId == q.questionId)
        )
      ) {
        const title = t('survey_answer_required').toString();
        const template = t('survey_enter_answer').toString();
        generalPurposeModalStore.setModal({
          title,
          template,
          variant: GeneralPurposeVariant.Decision,
        });
        return;
      }
      window.scrollTo(0, 0);
      isProcessing.value = true
      $q.loading.show()

      const page = surveyModule.currentPage;
      const surveyAnswers: SurveyAnswerRequest[] = [];

      const hoistedAnswers = responses.filter(
        (p: any) => p.permittedAnswer.renderingConfig.hoistAnswerToParentFreeResponseField
      );

      responses
        .filter((p: any) => !p.permittedAnswer.renderingConfig.hoistAnswerToParentFreeResponseField)
        .forEach(({ question, permittedAnswer, value }: any) => {
          const progressAnswer: SurveyProgressDenormalizedQuestionAndAnswerModel = {
            page,
            question,
            answerId: permittedAnswer.answerId,
            skipped: false,
          };

          if (
            permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.textbox ||
            permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.dropdown
          ) {
            progressAnswer.answerFreeformText = (value as string).toString();
          } else if (permittedAnswer.renderingConfig.type === SurveyComponentTypeEnum.number) {
            progressAnswer.answerId = parseInt(value as string, 10);
          } else if (question.renderingConfig.type === SurveyComponentQuestionTypeEnum.slider) {
            progressAnswer.answerId = parseInt(value as string);
          }

          const hoistedAnswer = hoistedAnswers.find(
            (p: any) => p.permittedAnswer.parentAnswerId === permittedAnswer.answerId
          );
          if (hoistedAnswer) {
            progressAnswer.answerFreeformText = hoistedAnswer.value as string;
          }

          surveyModule.setAddSurveyProgressAnswer(progressAnswer);

          surveyAnswers.push({
            questionId: question.questionId,
            questionTypeId: question.questionTypeId,
            answerId: progressAnswer.answerId,
            answerFreeformText: progressAnswer.answerFreeformText,
            skipped: progressAnswer.skipped,
          });
        });

      //question was skipped
      if (!responses.length) {
        surveyAnswers.push({
          questionId: page.questions[0].questionId,
          questionTypeId: page.questions[0].questionTypeId,
          skipped: true,
        });

        surveyModule.setAddSurveyProgressAnswer({
          page,
          question: page.questions[0],
          answerId: 0,
          skipped: true,
        });
      }

      const updateProgressResponse = await handleApiRequest(() =>
        updateSurveyProgress({
          eventDate: surveyModule.surveyProgress.eventDate,
          surveyId: surveyModule.surveyProgress.surveyId,
          tickSignature: surveyModule.surveyProgress.tickSignature,
          surveyResponseId: surveyModule.surveyProgress.surveyResponseId,
          languageId,
          surveyAnswers,
        }), null, $q
      );

      if (updateProgressResponse?.result) {
        surveyModule.setSyncPageVisibilityAndQuestionText(updateProgressResponse.result.surveyData.questionnaire.pageList);

        const isComplete = isLastPage.value || updateProgressResponse?.result.surveyMarkedComplete;

        surveyModule.setIsComplete(isComplete);

        if (!declineToComplete.value) {
          surveyModule.setCurrentPageToNextVisiblePage({});
        }

        surveyModule.setIsComplete(isComplete);

        // If the survey is complete, submit the survey and redirect
        if (isComplete) {
          onSubmitClick();
          dashboardStore.patient.interruptData.interrupts = dashboardStore.patient.interruptData.interrupts.filter((i) => i.surveyId !== props.surveyId);
          router.replace({ name: redirectRoute.value });
        }
      }
      isProcessing.value = false
      $q.loading.hide()
    }
  }

  async function onSubmitClick() {
    const languageId = sessionStore.user?.preferredLanguageId || 0;

    isProcessing.value = false
    $q.loading.show()

    let tickSignature = surveyModule.surveyProgress.tickSignature;
    if (surveyModule.surveyDefinition?.surveyType === SurveyTypeEnum.PMH) {
      tickSignature = (surveyModule.surveyDefinition.surveyId * -1).toString();
    }

    const updateProgressResponse = await handleApiRequest(() =>
      updateSurveyProgress({
        eventDate: surveyModule.surveyProgress.eventDate,
        surveyId: surveyModule.surveyProgress.surveyId,
        tickSignature: tickSignature,
        surveyResponseId: surveyModule.surveyProgress.surveyResponseId,
        languageId,
        surveyAnswers: [],
        isComplete: true,
      }), null, $q
    );

    // Update the task already loaded into the store as completed
    dashboardStore.markTaskCompleted({ id: props.surveyId, taskType: TaskType.survey });

    isProcessing.value = false
    $q.loading.hide()
  }

  async function onDeclineClick() {
    declineToComplete.value = true;
    onNextClick();
    // Remove the task
    dashboardStore.removeTask({ id: props.surveyId, taskType: TaskType.survey });
    router.replace({ name: redirectRoute.value });
  }
</script>

<template>
  <q-page class="survey" padding>
    <MultiViewLayout :loading="isLoading" :onBackClick="onLayoutBackClick"
                     :showBackArrow="false"
                     :boxWidth="BoxWidthEnum.xs">

      <!-- Heading Text -->
      <template #heading>
        <div>
          {{ headingText }}
        </div>
      </template>

      <template #default>
        <div class="">

          <!-- Survey Questions/Content -->
          <div v-show="!isLoading && !isComplete" class="col">
            <TheWizardPage @on-decline-click="onDeclineClick" @on-next-click="onNextClick" ref="page" :showHeaderTextBold="true" :showFooter="true" />

            <!-- Next button -->
            <div class="row justify-center">
              <zbm-btn @click="onNextClick" :color="$zb.enums.ButtonStdColorEnum.accent"
                       :height="$zb.enums.ButtonHeightEnum.lg" :width="$zb.enums.ButtonWidthEnum.wFull"
                       :label="t(buttonText)" data-testid="nextButton" />
            </div>
          </div>
          <!-- Survey Submit -->
          <TheSurveySubmit v-if="!isLoading && isComplete" />

          <!-- Progress bar -->
          <div class="row q-gutter-sm q-mt-xs q-mx-md">
            <div v-for="n in totalSteps" :key="n"
                 :class="['col rounded-borders simple-bar', { 'bg-grey-6': n === currentStep }]">
            </div>
          </div>

        </div>
      </template>

    </MultiViewLayout>
  </q-page>
</template>

<style>
  .simple-bar { height: 3px }
</style>
